import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TablePagination,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import TrainerBulkIntake from "./TrainerBulkIntake";
import { filterTrainerInventoryBoard, paginateTrainerInventoryBoard } from "./trainerInventoryBoardUtils";
import QuickTrainerIntake from "./QuickTrainerIntake";
import QuickTrainerAllocation from "./QuickTrainerAllocation";
import DeferredTrainerEbayReview from "./DeferredTrainerEbayReview";
import ReceiptEbayQuantityReview from "./ReceiptEbayQuantityReview";
import FC249EbayUpdateReview from "./FC249EbayUpdateReview";
import SkuQuantityVerification from "./SkuQuantityVerification";
import TrainerEbayErrorLogPanel from "./TrainerEbayErrorLogPanel";

const LISTING_STRATEGIES = [
  { value: "collectible_single", label: "Collectible Single" },
  { value: "trainer_single", label: "Trainer Single" },
  { value: "trainer_playset", label: "Trainer Playset" },
  { value: "trainer_both", label: "Trainer Both" },
  { value: "trainer_bulk", label: "Trainer Bulk" },
];

const REVIEW_STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "active", label: "Active" },
  { value: "review", label: "Review" },
  { value: "stale", label: "Stale" },
  { value: "hold", label: "Hold" },
  { value: "bulk_only", label: "Bulk Only" },
];

const DEMAND_BAND_OPTIONS = [
  { value: "", label: "All Demand" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const STOCK_ADJUSTMENT_REASON_OPTIONS = [
  { value: "sorted_from_bulk", label: "Sorted From Bulk" },
  { value: "found_stock", label: "Found Stock" },
  { value: "recount_adjustment", label: "Recount Adjustment" },
  { value: "damaged_removed", label: "Damaged / Removed" },
  { value: "manual_adjustment", label: "Manual Adjustment" },
];

const CARD_VARIANT_OPTIONS = ["Normal", "Reverse Holo", "Holo"];

const RECOMMENDED_ACTION_OPTIONS = [
  { value: "", label: "All Actions" },
  { value: "create listing", label: "Create Listing" },
  { value: "create playset listing", label: "Create Playset Listing" },
  { value: "increase qty", label: "Increase Qty" },
  { value: "plan playset", label: "Plan Playset" },
  { value: "playset candidate", label: "Playset Candidate" },
  { value: "top up single", label: "Top Up Single" },
  { value: "top up playset", label: "Top Up Playset" },
  { value: "link playset listing", label: "Link Playset Listing" },
  { value: "sync single allocation", label: "Sync Single Allocation" },
  { value: "review over allocation", label: "Review Over Allocation" },
  { value: "stale review", label: "Stale Review" },
  { value: "review demand drop", label: "Review Demand Drop" },
  { value: "review listed qty", label: "Review Listed Qty" },
  { value: "needs review", label: "Needs Review" },
  { value: "monitor", label: "Monitor" },
];

const parseInventoryNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const buildInventoryDraft = (row) => ({
  card_variant: row.card_variant || "Normal",
  quantity_listed: row.quantity_listed ?? 0,
  linked_listing_id: row.linked_listing_id ?? "",
  linked_playset_listing_id: row.linked_playset_listing_id ?? "",
  storage_location: row.storage_location || "",
  listing_strategy: row.listing_strategy || "trainer_single",
  target_single_quantity: row.target_single_quantity ?? 0,
  target_playset_quantity: row.target_playset_quantity ?? 0,
  review_status: row.review_status || "new",
  inventory_notes: row.inventory_notes || "",
  stock_adjustment_quantity: 0,
  stock_adjustment_reason: "sorted_from_bulk",
});

const buildPoIntakeDraft = (inventoryId) => ({
  open: true,
  inventoryId,
  purchase_request_id: "",
  quantity: 1,
  unit_cost: "",
  notes: "",
});

export default function TrainerInventory() {
  const [activeView, setActiveView] = useState("quick_intake");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [ebayErrorRefreshToken, setEbayErrorRefreshToken] = useState(0);
  const [deferredReviewRefreshToken, setDeferredReviewRefreshToken] = useState(0);
  const [inventoryRows, setInventoryRows] = useState([]);
  const [inventoryDraftsById, setInventoryDraftsById] = useState({});
  const [inventorySaveStatusById, setInventorySaveStatusById] = useState({});
  const [inventorySearchTerm, setInventorySearchTerm] = useState("");
  const [inventoryPage, setInventoryPage] = useState(0);
  const [inventoryPageSize, setInventoryPageSize] = useState(25);
  const [inventoryLoadError, setInventoryLoadError] = useState("");
  const [inventoryFilters, setInventoryFilters] = useState({
    demand_band: "",
    recommended_action: "",
    review_status: "",
  });

  const [poSources, setPoSources] = useState([]);
  const [poIntakeModal, setPoIntakeModal] = useState({
    open: false,
    inventoryId: null,
    purchase_request_id: "",
    quantity: 1,
    unit_cost: "",
    notes: "",
  });

  const loadInventoryOverview = (
    term = inventorySearchTerm,
    nextFilters = inventoryFilters
  ) => {
    axios
      .get("/api/trainer_inventory", {
        params: {
          search: term,
          demand_band: nextFilters.demand_band,
          recommended_action: nextFilters.recommended_action,
          review_status: nextFilters.review_status,
        },
      })
      .then((response) => {
        const data = response.data || [];
        setInventoryLoadError("");
        setInventoryPage(0);
        setInventoryRows(data);
        setInventoryDraftsById(
          data.reduce((acc, row) => {
            acc[row.id] = buildInventoryDraft(row);
            return acc;
          }, {})
        );
      })
      .catch((error) => {
        console.error("Trainer inventory load failed:", error);
        setInventoryLoadError(error.response?.data?.error || "Inventory search failed. Try refreshing.");
      });
  };

  const loadPoSources = () => {
    axios
      .get("/api/trainer_inventory/po_sources")
      .then((response) => setPoSources(response.data || []))
      .catch((error) => console.error("Trainer inventory PO source load failed:", error));
  };

  const refreshEbayErrors = () => {
    setEbayErrorRefreshToken((prev) => prev + 1);
  };

  useEffect(() => {
    loadInventoryOverview();
    loadPoSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateInventoryDraftField = (inventoryId, field, value) => {
    setInventoryDraftsById((prev) => ({
      ...prev,
      [inventoryId]: {
        ...prev[inventoryId],
        [field]: value,
      },
    }));
  };



  const adjustInventoryTarget = (inventoryId, field, delta) => {
    setInventoryDraftsById((prev) => {
      const currentDraft = prev[inventoryId] || {};
      const nextValue = Math.max(0, parseInventoryNumber(currentDraft[field], 0) + delta);
      return {
        ...prev,
        [inventoryId]: {
          ...currentDraft,
          [field]: nextValue,
        },
      };
    });
  };


  const adjustInventoryStockDraft = (inventoryId, delta) => {
    setInventoryDraftsById((prev) => {
      const currentDraft = prev[inventoryId] || {};
      const nextValue = parseInventoryNumber(currentDraft.stock_adjustment_quantity, 0) + delta;
      return {
        ...prev,
        [inventoryId]: {
          ...currentDraft,
          stock_adjustment_quantity: nextValue,
        },
      };
    });
  };

  const upsertInventoryRow = (updatedRow) => {
    setInventoryRows((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
    setInventoryDraftsById((prev) => ({
      ...prev,
      [updatedRow.id]: buildInventoryDraft(updatedRow),
    }));
  };

  const setInventoryStatus = (inventoryId, status) => {
    setInventorySaveStatusById((prev) => ({ ...prev, [inventoryId]: status }));
  };


  const selectedInventoryRow = useMemo(
    () => inventoryRows.find((row) => String(row.id) === String(poIntakeModal.inventoryId)) || null,
    [inventoryRows, poIntakeModal.inventoryId]
  );

  const selectedPoSource = useMemo(() => {
    if (!poIntakeModal.purchase_request_id) {
      return null;
    }
    return poSources.find(
      (source) => String(source.purchase_request_id) === String(poIntakeModal.purchase_request_id)
    ) || null;
  }, [poIntakeModal.purchase_request_id, poSources]);

  const openPoIntakeModal = (inventoryId) => {
    setPoIntakeModal(buildPoIntakeDraft(inventoryId));
  };

  const closePoIntakeModal = () => {
    setPoIntakeModal({
      open: false,
      inventoryId: null,
      purchase_request_id: "",
      quantity: 1,
      unit_cost: "",
      notes: "",
    });
  };

  const updatePoIntakeField = (field, value) => {
    setPoIntakeModal((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInventoryResponse = (inventoryId, responseData, fallbackStatus = "saved") => {
    const updatedRow = responseData?.row;
    const pushResults = responseData?.push_results;
    const adjustmentMessage = responseData?.adjustment_message;
    if (updatedRow) {
      upsertInventoryRow(updatedRow);
    }
    if (pushResults) {
      const pushMessages = Object.values(pushResults)
        .map((result) => result?.message)
        .filter(Boolean)
        .join(" | ");
      setInventoryStatus(inventoryId, pushMessages || fallbackStatus);
      return;
    }
    setInventoryStatus(inventoryId, adjustmentMessage || fallbackStatus);
  };

  const persistInventoryDraft = async (inventoryId) => {
    const payload = inventoryDraftsById[inventoryId] || {};
    const response = await axios.patch(`/api/trainer_inventory/${inventoryId}`, payload);
    handleInventoryResponse(inventoryId, response.data, "saved");
    return response.data;
  };

  const runInventoryRowAction = async (inventoryId, endpoint) => {
    setInventoryStatus(inventoryId, "saving");
    try {
      const response = await axios.post(`/api/trainer_inventory/${inventoryId}/${endpoint}`);
      handleInventoryResponse(inventoryId, response.data, "saved");
    } catch (error) {
      setInventoryStatus(inventoryId, error.response?.data?.error || "error");
    }
  };

  const saveInventoryRow = async (inventoryId) => {
    setInventoryStatus(inventoryId, "saving");
    try {
      await persistInventoryDraft(inventoryId);
    } catch (error) {
      setInventoryStatus(inventoryId, error.response?.data?.error || "error");
    }
  };

  const applyMixSuggestion = async (inventoryId) => {
    runInventoryRowAction(inventoryId, "apply_mix_suggestion");
  };

  const syncFromListings = async (inventoryId) => {
    runInventoryRowAction(inventoryId, "sync_from_listings");
  };

  const applyTargetAllocation = async (inventoryId) => {
    runInventoryRowAction(inventoryId, "apply_target_allocation");
  };

  const autoLinkPlayset = async (inventoryId) => {
    runInventoryRowAction(inventoryId, "auto_link_playset");
  };

  const pushTargetsToListings = async (inventoryId) => {
    setInventoryStatus(inventoryId, "saving");
    try {
      await persistInventoryDraft(inventoryId);
      const response = await axios.post(`/api/trainer_inventory/${inventoryId}/push_targets_to_listings`);
      handleInventoryResponse(inventoryId, response.data, "saved");
    } catch (error) {
      setInventoryStatus(inventoryId, error.response?.data?.error || "error");
    }
  };

  const applyStockAdjustment = async (inventoryId) => {
    setInventoryStatus(inventoryId, "saving");
    try {
      const draft = inventoryDraftsById[inventoryId] || {};
      const response = await axios.post(`/api/trainer_inventory/${inventoryId}/adjust_stock`, {
        stock_adjustment_quantity: parseInventoryNumber(draft.stock_adjustment_quantity, 0),
        stock_adjustment_reason: draft.stock_adjustment_reason || "sorted_from_bulk",
      });
      handleInventoryResponse(inventoryId, response.data, "saved");
      setInventoryDraftsById((prev) => ({
        ...prev,
        [inventoryId]: {
          ...(prev[inventoryId] || buildInventoryDraft(response.data?.row || {})),
          stock_adjustment_quantity: 0,
          stock_adjustment_reason: draft.stock_adjustment_reason || "sorted_from_bulk",
        },
      }));
    } catch (error) {
      setInventoryStatus(inventoryId, error.response?.data?.error || "error");
    }
  };

  const addStockFromPo = async () => {
    const inventoryId = poIntakeModal.inventoryId;
    if (!inventoryId) {
      return;
    }
    setInventoryStatus(inventoryId, "saving");
    try {
      const response = await axios.post(`/api/trainer_inventory/${inventoryId}/add_stock_from_po`, {
        purchase_request_id: poIntakeModal.purchase_request_id || null,
        quantity: parseInventoryNumber(poIntakeModal.quantity, 0),
        unit_cost: poIntakeModal.unit_cost,
        notes: poIntakeModal.notes || "",
        card_variant: selectedInventoryRow?.card_variant || "Normal",
      });
      handleInventoryResponse(inventoryId, response.data, "saved");
      closePoIntakeModal();
      loadPoSources();
    } catch (error) {
      setInventoryStatus(inventoryId, error.response?.data?.error || "error");
    }
  };

  const createPlaysetDraft = async (inventoryId) => {
    runInventoryRowAction(inventoryId, "create_playset_draft");
  };

  const createSingleDraft = async (inventoryId) => {
    runInventoryRowAction(inventoryId, "create_single_draft");
  };

  const inventoryCounts = useMemo(
    () => ({
      total: inventoryRows.length,
      highDemand: inventoryRows.filter((row) => row.demand_band === "high").length,
      stale: inventoryRows.filter((row) =>
        ["stale review", "review demand drop"].includes(row.recommended_action)
      ).length,
      topUp: inventoryRows.filter((row) => row.recommended_action === "increase qty").length,
      playset: inventoryRows.filter((row) => ["plan playset", "playset candidate", "link playset listing", "create playset listing", "top up playset"].includes(row.recommended_action)).length,
    }),
    [inventoryRows]
  );

  // Paginate the complete server-filtered result; never truncate after the first 50.
  // Local exact-print matching additionally protects against a stale/general search response.
  const boardFilteredRows = useMemo(() => filterTrainerInventoryBoard(inventoryRows, inventorySearchTerm),
    [inventoryRows, inventorySearchTerm]);
  const boardRows = useMemo(() => paginateTrainerInventoryBoard(boardFilteredRows, inventoryPage, inventoryPageSize),
    [boardFilteredRows, inventoryPage, inventoryPageSize]);

  return (
    <Box sx={{ height: "80vh", width: "100%" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Playable Card Operations</Typography>
          <Typography variant="body2" color="text.secondary">
            Two steps: receive your cards in Quick Intake, then open eBay Update. eBay quantities never change merely by viewing or receiving cards.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => loadInventoryOverview()}>
            Refresh Inventory
          </Button>
          <Button variant="outlined" onClick={() => loadPoSources()}>
            Refresh PO Sources
          </Button>
        </Stack>
      </Stack>

      <Tabs variant="scrollable" scrollButtons="auto" value={activeView}
        onChange={(_, next) => setActiveView(next)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab value="quick_intake" label="1. Quick Intake" />
        <Tab value="allocation_ebay" label="2. eBay Update" />
        {advancedOpen && <Tab value="fc246_deferred" label="Legacy · FC246 Allocation & eBay" />}
        {advancedOpen && <Tab value="fc247_receipt" label="Legacy · FC247 Receipt Reconciliation" />}
        {advancedOpen && <Tab value="inventory" label="Legacy · Inventory Board" />}
        {advancedOpen && <Tab value="bulk_intake" label="Legacy · Bulk Intake" />}
        {advancedOpen && <Tab value="sku_verification" label="Legacy · SKU Verification" />}
      </Tabs>
      <Button size="small" variant="text" onClick={() => {
        if (advancedOpen && !["quick_intake", "allocation_ebay"].includes(activeView)) setActiveView("quick_intake");
        setAdvancedOpen(!advancedOpen);
      }}>{advancedOpen ? "Hide Archive / Legacy" : "Open Archive / Legacy"}</Button>
      {advancedOpen && activeView !== "quick_intake" && <TrainerEbayErrorLogPanel refreshToken={ebayErrorRefreshToken} />}
      {activeView === "quick_intake" && <QuickTrainerIntake onGoToEbayUpdate={() => setActiveView("allocation_ebay")} />}
      {activeView === "allocation_ebay" && <FC249EbayUpdateReview />}
      {advancedOpen && activeView === "fc247_receipt" && <Stack spacing={1}><Alert severity="warning">Legacy FC247 reconciliation and recovery. Do not start a parallel claim for a receipt being handled by FC249. Use this area only for historical review or supervised recovery.</Alert><ReceiptEbayQuantityReview /></Stack>}
      {advancedOpen && activeView === "fc246_deferred" && <Stack spacing={3} sx={{ mt: 2, pb: 3 }}>
        <Alert severity="warning">Advanced FC246 historical allocation/reconciliation. Do not use for receipts already claimed by eBay Quantity Review.</Alert>
        <QuickTrainerAllocation onOpenInventory={(cardName) => {
          setInventorySearchTerm(cardName); loadInventoryOverview(cardName); setAdvancedOpen(true); setActiveView("inventory");
        }} onAllocationApplied={() => setDeferredReviewRefreshToken((n) => n + 1)} />
        <DeferredTrainerEbayReview key={deferredReviewRefreshToken} />
      </Stack>}
      {advancedOpen && activeView === "sku_verification" && <SkuQuantityVerification />}
      {activeView === "bulk_intake" && <TrainerBulkIntake onEbayErrorLogged={refreshEbayErrors} />}
      {activeView === "inventory" && (
      <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Search Trainer Inventory"
            variant="outlined"
            size="small"
            fullWidth
            value={inventorySearchTerm}
            onChange={(e) => { setInventorySearchTerm(e.target.value); setInventoryPage(0); }}
            helperText="Search exact name and printed number; press Enter or Search. All results are paginated."
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                loadInventoryOverview(e.target.value);
              }
            }}
          />
          <Button size="small" onClick={() => loadInventoryOverview(inventorySearchTerm)}>Search</Button>
          <Button size="small" onClick={() => { setInventorySearchTerm(""); loadInventoryOverview(""); }}>Clear</Button>
        </Grid>
        <Grid item xs={12} md={2.66}>
          <FormControl size="small" fullWidth>
            <InputLabel>Demand</InputLabel>
            <Select
              value={inventoryFilters.demand_band}
              label="Demand"
              onChange={(e) => {
                const next = { ...inventoryFilters, demand_band: e.target.value };
                setInventoryFilters(next);
                loadInventoryOverview(inventorySearchTerm, next);
              }}
            >
              {DEMAND_BAND_OPTIONS.map((option) => (
                <MenuItem key={option.value || "all-demand"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2.66}>
          <FormControl size="small" fullWidth>
            <InputLabel>Recommended Action</InputLabel>
            <Select
              value={inventoryFilters.recommended_action}
              label="Recommended Action"
              onChange={(e) => {
                const next = { ...inventoryFilters, recommended_action: e.target.value };
                setInventoryFilters(next);
                loadInventoryOverview(inventorySearchTerm, next);
              }}
            >
              {RECOMMENDED_ACTION_OPTIONS.map((option) => (
                <MenuItem key={option.value || "all-actions"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2.66}>
          <FormControl size="small" fullWidth>
            <InputLabel>Review Status</InputLabel>
            <Select
              value={inventoryFilters.review_status}
              label="Review Status"
              onChange={(e) => {
                const next = { ...inventoryFilters, review_status: e.target.value };
                setInventoryFilters(next);
                loadInventoryOverview(inventorySearchTerm, next);
              }}
            >
              <MenuItem value="">All Review Status</MenuItem>
              {REVIEW_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Alert severity="info" icon={false}>Total {inventoryCounts.total}</Alert>
        </Grid>
        <Grid item xs={6} md={3}>
          <Alert severity="success" icon={false}>High demand {inventoryCounts.highDemand}</Alert>
        </Grid>
        <Grid item xs={6} md={3}>
          <Alert severity="warning" icon={false}>Top-up {inventoryCounts.topUp}</Alert>
        </Grid>
        <Grid item xs={6} md={3}>
          <Alert severity="info" icon={false}>Playset {inventoryCounts.playset}</Alert>
        </Grid>
        <Grid item xs={6} md={3}>
          <Alert severity="error" icon={false}>Stale {inventoryCounts.stale}</Alert>
        </Grid>
      </Grid>

      {inventoryLoadError && <Alert severity="error" role="alert">{inventoryLoadError}</Alert>}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Showing {boardRows.length ? inventoryPage * inventoryPageSize + 1 : 0}–{Math.min((inventoryPage + 1) * inventoryPageSize, boardFilteredRows.length)} of {boardFilteredRows.length} matching cards
      </Typography>
      <TablePagination component="div" count={boardFilteredRows.length} page={Math.min(inventoryPage, Math.max(0, Math.ceil(boardFilteredRows.length / inventoryPageSize) - 1))}
        rowsPerPage={inventoryPageSize} rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_, page) => setInventoryPage(page)}
        onRowsPerPageChange={(event) => { setInventoryPageSize(Number(event.target.value)); setInventoryPage(0); }} />
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            {boardRows.map((row) => {
              const draft = inventoryDraftsById[row.id] || buildInventoryDraft(row);
              const saveStatus = inventorySaveStatusById[row.id];
              const calculatedAvailable = Math.max(
                0,
                Number(row.quantity_on_hand || 0) - Number(draft.quantity_listed || 0)
              );
              const draftTargetSingles = Math.max(0, parseInventoryNumber(draft.target_single_quantity, 0));
              const draftTargetPlaysets = Math.max(0, parseInventoryNumber(draft.target_playset_quantity, 0));
              const reservedPreview = draftTargetSingles + (draftTargetPlaysets * 4);
              const projectedUnallocated = Math.max(0, Number(row.quantity_on_hand || 0) - reservedPreview);
              return (
                <Card key={row.id} variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        gap={1}
                      >
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="subtitle1">{row.card_name}</Typography>
                            <Chip size="small" label={row.card_variant || "Normal"} />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            Demand {row.demand_band || "-"} ({row.demand_score ?? 0}) | Action {row.recommended_action || "-"} | Last sale {row.days_since_last_sale ?? "-"} days ago
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip size="small" label={`Avail ${row.quantity_available ?? 0}`} />
                          <Chip size="small" label={`Listed ${row.quantity_listed ?? 0}`} />
                          <Chip size="small" label={`Reserved ${row.reserved_units ?? 0}`} />
                          <Chip size="small" label={`Unallocated ${row.unallocated_units ?? 0}`} />
                          <Chip size="small" label={`Sales30 ${row.sales_count_30 ?? 0}`} />
                          <Chip size="small" label={`Sales90 ${row.sales_count_90 ?? 0}`} />
                          {row.linked_listing_id && <Chip size="small" color="info" label={`Single #${row.linked_listing_id}`} />}
                          {row.linked_playset_listing_id && <Chip size="small" color="secondary" label={`Playset #${row.linked_playset_listing_id}`} />}
                          {row.over_allocated_units > 0 && <Chip size="small" color="error" label={`Over ${row.over_allocated_units}`} />}
                          {saveStatus === "saving" && <Chip size="small" color="info" label="Saving" />}
                          {saveStatus === "saved" && <Chip size="small" color="success" label="Saved" />}
                          {saveStatus && !["saving", "saved"].includes(saveStatus) && (
                            <Chip size="small" color="error" label={String(saveStatus).slice(0, 28)} />
                          )}
                        </Stack>
                      </Stack>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Variant / Finish</InputLabel>
                            <Select
                              label="Variant / Finish"
                              value={draft.card_variant || "Normal"}
                              onChange={(e) => updateInventoryDraftField(row.id, "card_variant", e.target.value)}
                            >
                              {CARD_VARIANT_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Qty Listed"
                            value={draft.quantity_listed}
                            onChange={(e) => updateInventoryDraftField(row.id, "quantity_listed", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Stock Delta"
                            value={draft.stock_adjustment_quantity}
                            onChange={(e) => updateInventoryDraftField(row.id, "stock_adjustment_quantity", e.target.value)}
                            helperText="Use positive to add sorted stock or negative to reduce on hand."
                          />
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button size="small" variant="outlined" onClick={() => adjustInventoryStockDraft(row.id, -1)}>
                              -1 On Hand
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => adjustInventoryStockDraft(row.id, 1)}>
                              +1 On Hand
                            </Button>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Single Listing ID"
                            value={draft.linked_listing_id}
                            onChange={(e) => updateInventoryDraftField(row.id, "linked_listing_id", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Playset Listing ID"
                            value={draft.linked_playset_listing_id}
                            onChange={(e) => updateInventoryDraftField(row.id, "linked_playset_listing_id", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Storage Location"
                            value={draft.storage_location}
                            onChange={(e) => updateInventoryDraftField(row.id, "storage_location", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Stock Reason</InputLabel>
                            <Select
                              value={draft.stock_adjustment_reason || "sorted_from_bulk"}
                              label="Stock Reason"
                              onChange={(e) => updateInventoryDraftField(row.id, "stock_adjustment_reason", e.target.value)}
                            >
                              {STOCK_ADJUSTMENT_REASON_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Strategy</InputLabel>
                            <Select
                              value={draft.listing_strategy}
                              label="Strategy"
                              onChange={(e) => updateInventoryDraftField(row.id, "listing_strategy", e.target.value)}
                            >
                              {LISTING_STRATEGIES.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Target Singles"
                            value={draft.target_single_quantity}
                            onChange={(e) => updateInventoryDraftField(row.id, "target_single_quantity", e.target.value)}
                          />
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button size="small" variant="outlined" onClick={() => adjustInventoryTarget(row.id, "target_single_quantity", -1)}>
                              -1 Single
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => adjustInventoryTarget(row.id, "target_single_quantity", 1)}>
                              +1 Single
                            </Button>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Target Playsets"
                            value={draft.target_playset_quantity}
                            onChange={(e) => updateInventoryDraftField(row.id, "target_playset_quantity", e.target.value)}
                          />
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button size="small" variant="outlined" onClick={() => adjustInventoryTarget(row.id, "target_playset_quantity", -1)}>
                              -1 Playset
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => adjustInventoryTarget(row.id, "target_playset_quantity", 1)}>
                              +1 Playset
                            </Button>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={2.5}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Review Status</InputLabel>
                            <Select
                              value={draft.review_status}
                              label="Review Status"
                              onChange={(e) => updateInventoryDraftField(row.id, "review_status", e.target.value)}
                            >
                              {REVIEW_STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={5.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Inventory Notes"
                            value={draft.inventory_notes}
                            onChange={(e) => updateInventoryDraftField(row.id, "inventory_notes", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Alert severity="info" icon={false}>
                            Trainer-only view. Use Add On Hand From PO to open a modal, choose the PO source, set quantity, adjust unit cost when needed, and record transfer notes.
                          </Alert>
                        </Grid>
                      </Grid>

                      <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
                        <Alert severity="info" icon={false} sx={{ flex: 1 }}>
                          Variant: {row.card_variant || "Normal"} | On hand: {row.quantity_on_hand ?? 0} | Projected on hand after stock delta: {Math.max(0, Number(row.quantity_on_hand || 0) + parseInventoryNumber(draft.stock_adjustment_quantity, 0))} | Available after save: {calculatedAvailable} | Avg cost: {row.avg_unit_cost ?? 0} | Single SKU: {row.linked_listing_sku || "-"} ({row.linked_ebay_quantity ?? 0}) | Playset SKU: {row.linked_playset_listing_sku || "-"} ({row.linked_playset_ebay_quantity ?? 0})
                        </Alert>
                      </Stack>

                      <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
                        <Alert severity={row.over_allocated_units > 0 ? "error" : "warning"} icon={false} sx={{ flex: 1 }}>
                          Suggested mix: {row.suggested_single_quantity ?? 0} singles and {row.suggested_playset_quantity ?? 0} playsets | Reserved now: {row.reserved_units ?? 0} units | Reserved preview: {reservedPreview} units | Unassigned: {row.unallocated_units ?? 0} units | Projected unallocated: {projectedUnallocated} units | Single status: {row.single_listing_status || "-"} | Playset status: {row.playset_listing_status || "-"}
                        </Alert>
                      </Stack>

                      <Stack direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                        <Button variant="outlined" onClick={() => syncFromListings(row.id)}>
                          Sync From Listings
                        </Button>
                        <Button variant="outlined" onClick={() => applyMixSuggestion(row.id)}>
                          Apply Suggested Mix
                        </Button>
                        <Button variant="outlined" onClick={() => applyTargetAllocation(row.id)}>
                          Apply Target Allocation
                        </Button>
                        <Button variant="outlined" onClick={() => autoLinkPlayset(row.id)}>
                          Auto-Link Playset
                        </Button>
                        <Button variant="outlined" onClick={() => createPlaysetDraft(row.id)}>
                          Create Playset Draft
                        </Button>
                        {!row.linked_listing_id && (
                          <Button variant="outlined" onClick={() => createSingleDraft(row.id)}>
                            Create Single Draft
                          </Button>
                        )}
                        <Button variant="outlined" onClick={() => applyStockAdjustment(row.id)}>
                          Apply Stock Delta
                        </Button>
                        <Button variant="outlined" onClick={() => openPoIntakeModal(row.id)}>
                          Add On Hand From PO
                        </Button>
                        <Button variant="outlined" onClick={() => pushTargetsToListings(row.id)}>
                          Push Targets To Listings
                        </Button>
                        <Button variant="contained" onClick={() => saveInventoryRow(row.id)}>
                          Save Inventory Control
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
            {boardFilteredRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No Trainer inventory rows match the current filters.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={poIntakeModal.open} onClose={closePoIntakeModal} fullWidth maxWidth="sm">
        <DialogTitle>Add On Hand From PO</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" icon={false}>
              Select any received PO source with remaining quantity, set the quantity to move into the selected {selectedInventoryRow?.card_variant || "Normal"} Trainer Inventory row, and adjust unit cost if the PO line came from a bulk lot.
            </Alert>
            <FormControl size="small" fullWidth>
              <InputLabel>PO Source</InputLabel>
              <Select
                value={poIntakeModal.purchase_request_id || ""}
                label="PO Source"
                onChange={(e) => {
                  const nextValue = e.target.value;
                  const nextSource = poSources.find(
                    (source) => String(source.purchase_request_id) === String(nextValue)
                  );
                  setPoIntakeModal((prev) => ({
                    ...prev,
                    purchase_request_id: nextValue,
                    quantity: prev.quantity || 1,
                    unit_cost: nextSource?.unit_cost ?? "",
                  }));
                }}
              >
                <MenuItem value="">Select PO source</MenuItem>
                {poSources.map((source) => (
                  <MenuItem key={source.purchase_request_id} value={source.purchase_request_id}>
                    {`PO #${source.purchase_order_id} / PR #${source.purchase_request_id} / ${source.card_name || "-"} / ${source.card_variant || "Normal"} / Rem ${source.quantity_remaining_to_allocate}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedPoSource && (
              <Alert severity="info" icon={false}>
                Supplier: {selectedPoSource.supplier_name || "-"} | Source type: {selectedPoSource.source_type || "-"} | Received: {selectedPoSource.quantity_received ?? 0} | Remaining to allocate: {selectedPoSource.quantity_remaining_to_allocate ?? 0} | Default PO unit cost: {selectedPoSource.unit_cost ?? 0} | Any received PO source can be selected here.
              </Alert>
            )}
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Quantity to Add"
              value={poIntakeModal.quantity}
              onChange={(e) => updatePoIntakeField("quantity", e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Unit Cost"
              value={poIntakeModal.unit_cost}
              onChange={(e) => updatePoIntakeField("unit_cost", e.target.value)}
              helperText="Prefilled from the PO source. Override this when allocating stock from a bulk lot."
            />
            <TextField
              fullWidth
              size="small"
              label="Transfer Notes"
              value={poIntakeModal.notes}
              onChange={(e) => updatePoIntakeField("notes", e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePoIntakeModal}>Cancel</Button>
          <Button variant="contained" onClick={addStockFromPo}>Apply PO Intake</Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </Box>
  );
}
